/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

/**
 * The lens, sensor and recording format a device is planned with
 * (camera-simulation.md §4).
 *
 * Planning data on the device row, never in the shadow: nothing here reaches a
 * body, so none of it is a gateway's business and no shadow schema bump is due.
 */

export interface FormatSpec {
  id: string;
  label: string;
  width_px: number;
  height_px: number;
  /** How much narrower than the sensor this format reads, e.g. 1.24 for a cropped 8K. */
  crop?: number;
}

export interface BodySpec {
  model: string;
  sensor_mm: { width: number; height: number };
  /** The first entry is what a device with no optics records. */
  formats: readonly FormatSpec[];
}

export interface LensSpec {
  id: string;
  name: string;
  focal_mm: { min: number; max: number };
  /** Widest and narrowest f-number. Every zoom listed is constant-aperture. */
  aperture: { widest: number; narrowest: number };
  /** The worst case across the zoom range, so a valid distance is valid at every focal length. */
  min_focus_m: number;
}

const UHD: FormatSpec = { id: '4k', label: '4K UHD', width_px: 3840, height_px: 2160 };
const FHD: FormatSpec = { id: 'fhd', label: 'Full HD', width_px: 1920, height_px: 1080 };
const EIGHT_K: FormatSpec = { id: '8k', label: '8K', width_px: 7680, height_px: 4320 };

const still = (width: number, height: number): FormatSpec => ({
  id: 'still',
  label: 'Still · full sensor',
  width_px: width,
  height_px: height,
});

/** Indicative values; to be checked against the spec sheets (camera-simulation.md §11). */
export const BODIES: readonly BodySpec[] = [
  {
    model: 'ILX-LR1',
    sensor_mm: { width: 35.7, height: 23.8 },
    formats: [still(9504, 6336), { id: 'still-16x9', label: 'Still · 16:9', width_px: 9504, height_px: 5344 }],
  },
  { model: 'ILCE-1', sensor_mm: { width: 35.9, height: 24.0 }, formats: [still(8640, 5760), EIGHT_K, UHD, FHD] },
  {
    model: 'ILCE-7RM5',
    sensor_mm: { width: 35.7, height: 23.8 },
    formats: [still(9504, 6336), { ...EIGHT_K, crop: 1.24 }, UHD, FHD],
  },
  { model: 'ILCE-7CR', sensor_mm: { width: 35.7, height: 23.8 }, formats: [still(9504, 6336), UHD, FHD] },
  { model: 'ILCE-7M4', sensor_mm: { width: 35.9, height: 23.9 }, formats: [still(7008, 4672), UHD, FHD] },
  { model: 'ILCE-9M3', sensor_mm: { width: 35.6, height: 23.8 }, formats: [still(6000, 4000), UHD, FHD] },
  { model: 'ILCE-6700', sensor_mm: { width: 23.3, height: 15.5 }, formats: [still(6192, 4128), UHD, FHD] },
];

/** What a model this catalog does not know is simulated as. The inspector says so. */
export const GENERIC_BODY: BodySpec = {
  model: 'generic',
  sensor_mm: { width: 36, height: 24 },
  formats: [still(6000, 4000), UHD, FHD],
};

/** The lens a device with no optics is drawn with: the 50 mm the editor always assumed. */
export const DEFAULT_LENS_ID = 'generic-50';

export const LENSES: readonly LensSpec[] = [
  {
    id: DEFAULT_LENS_ID,
    name: '50 mm (generic)',
    focal_mm: { min: 50, max: 50 },
    aperture: { widest: 1.8, narrowest: 22 },
    min_focus_m: 0.45,
  },
  {
    id: 'sel1635gm2',
    name: 'FE 16-35mm F2.8 GM II',
    focal_mm: { min: 16, max: 35 },
    aperture: { widest: 2.8, narrowest: 22 },
    min_focus_m: 0.22,
  },
  {
    id: 'sel2470gm2',
    name: 'FE 24-70mm F2.8 GM II',
    focal_mm: { min: 24, max: 70 },
    aperture: { widest: 2.8, narrowest: 22 },
    min_focus_m: 0.3,
  },
  {
    id: 'sel2470z',
    name: 'FE 24-70mm F4 ZA OSS',
    focal_mm: { min: 24, max: 70 },
    aperture: { widest: 4, narrowest: 22 },
    min_focus_m: 0.4,
  },
  {
    id: 'sel70200gm2',
    name: 'FE 70-200mm F2.8 GM OSS II',
    focal_mm: { min: 70, max: 200 },
    aperture: { widest: 2.8, narrowest: 22 },
    min_focus_m: 0.82,
  },
  {
    id: 'sel24f14gm',
    name: 'FE 24mm F1.4 GM',
    focal_mm: { min: 24, max: 24 },
    aperture: { widest: 1.4, narrowest: 16 },
    min_focus_m: 0.24,
  },
  {
    id: 'sel35f14gm',
    name: 'FE 35mm F1.4 GM',
    focal_mm: { min: 35, max: 35 },
    aperture: { widest: 1.4, narrowest: 16 },
    min_focus_m: 0.27,
  },
  {
    id: 'sel50f12gm',
    name: 'FE 50mm F1.2 GM',
    focal_mm: { min: 50, max: 50 },
    aperture: { widest: 1.2, narrowest: 16 },
    min_focus_m: 0.4,
  },
  {
    id: 'sel85f14gm2',
    name: 'FE 85mm F1.4 GM II',
    focal_mm: { min: 85, max: 85 },
    aperture: { widest: 1.4, narrowest: 16 },
    min_focus_m: 0.85,
  },
];

/** Third stops, the steps a body's aperture dial clicks through. */
export const APERTURE_STOPS: readonly number[] = [
  1.2, 1.4, 1.6, 1.8, 2, 2.2, 2.5, 2.8, 3.2, 3.5, 4, 4.5, 5, 5.6, 6.3, 7.1, 8, 9, 10, 11, 13, 14, 16, 18, 20, 22,
];

export const bodyOf = (model: string): BodySpec => BODIES.find((body) => body.model === model) ?? GENERIC_BODY;

export const lensOf = (lensId: string): LensSpec | undefined => LENSES.find((lens) => lens.id === lensId);

export const OpticsSchema = z
  .object({
    lens_id: z.string().min(1).max(60),
    focal_length_mm: z.number().positive().max(2000),
    aperture_f: z.number().min(0.7).max(64),
    focus_distance_m: z.number().positive().max(10_000),
    format_id: z.string().min(1).max(40),
  })
  .strict();
export type Optics = z.infer<typeof OpticsSchema>;

/** Three metres is a studio's working distance, and inside every lens's range here. */
const DEFAULT_FOCUS_M = 3;

/** What a device with no stored optics is drawn with. */
export const defaultOptics = (model: string): Optics => ({
  lens_id: DEFAULT_LENS_ID,
  focal_length_mm: 50,
  aperture_f: 4,
  focus_distance_m: DEFAULT_FOCUS_M,
  format_id: bodyOf(model).formats[0].id,
});

/** Floating point from a slider must not fail a bound it visibly sits on. */
const TOLERANCE = 1e-6;

/**
 * Why these optics cannot go on this body, or undefined when they can.
 *
 * The same check the UI's controls are bounded by, so a value the inspector
 * offers is never refused here and a value a script invents is.
 */
export const validateOptics = (model: string, optics: Optics): string | undefined => {
  const lens = lensOf(optics.lens_id);
  if (!lens) {
    return `unknown lens ${optics.lens_id}`;
  }
  const { min, max } = lens.focal_mm;
  if (optics.focal_length_mm < min - TOLERANCE || optics.focal_length_mm > max + TOLERANCE) {
    return min === max
      ? `${lens.name} is a ${min} mm prime`
      : `${lens.name} zooms from ${min} to ${max} mm, not ${optics.focal_length_mm} mm`;
  }
  const { widest, narrowest } = lens.aperture;
  if (optics.aperture_f < widest - TOLERANCE || optics.aperture_f > narrowest + TOLERANCE) {
    return `${lens.name} stops from F${widest} to F${narrowest}, not F${optics.aperture_f}`;
  }
  if (optics.focus_distance_m < lens.min_focus_m - TOLERANCE) {
    return `${lens.name} focuses no closer than ${lens.min_focus_m} m`;
  }
  const body = bodyOf(model);
  if (!body.formats.some((format) => format.id === optics.format_id)) {
    return `${model} does not record ${optics.format_id}`;
  }
  return undefined;
};
